import {
  DEFINITION_MAX_WORDS,
  DEFINITION_MIN_WORDS,
  countWords,
  easeSignals,
  estimateDifficulty,
  findAmbiguityRisks,
  findWordFamilyWarnings,
  normalizedLengthOf,
  withoutExaminedPairs,
} from "@lexireveal/content-schema";
import type { ContentEntry, EaseSignal, ExaminedPair } from "@lexireveal/content-schema";
import couplesExamines from "@lexireveal/content-schema/content/fr/couples-examines.json";
import { MIN_ANSWER_LENGTH, normalizeAnswer } from "@lexireveal/game-engine";

/**
 * Les contrôles éditoriaux du dépôt, exécutés dans le navigateur pendant que
 * l'administrateur écrit (ADR-0005 : ils « ne changent pas de place »).
 *
 * Trois niveaux, comme la barrière des révisions (`pnpm revisions verifier`) :
 * - **échec** : la définition casse une règle du dépôt ; elle ne se publie pas ;
 * - **à juger** : un signal que le jeu devient peut-être trop simple, ou un
 *   couple confondable. La machine ne sait pas décider ; elle montre, et
 *   l'administrateur confirme l'avoir vu ;
 * - **information** : la difficulté estimée d'après la structure du mot.
 */

export type Brouillon = {
  readonly id: string;
  readonly mot: string;
  readonly definition: string;
  readonly variantes: readonly string[];
};

export type Controles = {
  readonly nombreDeMots: number;
  readonly echecs: readonly string[];
  readonly signaux: readonly EaseSignal[];
  readonly couples: readonly string[];
  readonly difficulteEstimee: { readonly note: number; readonly raisons: readonly string[] } | null;
};

const examines = couplesExamines as unknown as readonly ExaminedPair[];

function versEntree(brouillon: Brouillon): ContentEntry {
  return {
    id: brouillon.id,
    locale: "fr",
    answer: brouillon.mot,
    acceptedAnswers: [...brouillon.variantes],
    definition: brouillon.definition,
    culturalNote: null,
    difficulty: 3,
    editorialStatus: "approved",
    originalityConfirmed: true,
    draftedBy: "human",
    reviewedBy: null,
  };
}

const cleCouple = (a: string, b: string) => [a, b].sort().join("|");

/**
 * Couples confondables que la correction crée : même longueur, définitions que
 * rien ne sépare. Ceux qui existaient déjà, ou qu'un humain a jugés distincts
 * (`couples-examines.json`), ne sont pas remontés.
 */
function nouveauxCouples(brouillon: Brouillon, corpus: readonly ContentEntry[]): string[] {
  const longueur = normalizedLengthOf(brouillon.mot);
  const memeLongueur = corpus.filter((e) => e.id !== brouillon.id && normalizedLengthOf(e.answer) === longueur);
  const actuelle = corpus.find((e) => e.id === brouillon.id);

  const avant = new Set(
    (actuelle === undefined ? [] : findAmbiguityRisks([...memeLongueur, actuelle])).map((r) =>
      cleCouple(r.first.id, r.second.id),
    ),
  );
  const apres = withoutExaminedPairs(findAmbiguityRisks([...memeLongueur, versEntree(brouillon)]), examines);

  return apres
    .filter((r) => (r.first.id === brouillon.id || r.second.id === brouillon.id) && !avant.has(cleCouple(r.first.id, r.second.id)))
    .map((r) => {
      const autre = r.first.id === brouillon.id ? r.second : r.first;
      return `${autre.answer} (${autre.id}) : « ${autre.definition} »`;
    });
}

export function controler(brouillon: Brouillon, corpus: readonly ContentEntry[] | null): Controles {
  const echecs: string[] = [];
  const mot = normalizeAnswer(brouillon.mot);
  const nombreDeMots = countWords(brouillon.definition);

  if (mot.length < MIN_ANSWER_LENGTH) {
    echecs.push(`Mot de ${mot.length} lettres : il en faut au moins ${MIN_ANSWER_LENGTH} pour la révélation.`);
  }
  if (nombreDeMots < DEFINITION_MIN_WORDS || nombreDeMots > DEFINITION_MAX_WORDS) {
    echecs.push(`Définition de ${nombreDeMots} mots : le guide demande ${DEFINITION_MIN_WORDS} à ${DEFINITION_MAX_WORDS}.`);
  }
  if (mot.length > 0 && normalizeAnswer(brouillon.definition).includes(mot)) {
    echecs.push("La définition contient la réponse.");
  }
  const entree = versEntree(brouillon);
  for (const avertissement of findWordFamilyWarnings(entree)) {
    echecs.push(`Famille du mot : ${avertissement.message}.`);
  }
  for (const variante of brouillon.variantes) {
    if (normalizeAnswer(variante).length !== mot.length) {
      echecs.push(`Variante « ${variante} » : pas la même longueur que le mot, la grille ne l'accepterait pas.`);
    }
  }

  let difficulteEstimee: Controles["difficulteEstimee"] = null;
  if (mot.length >= MIN_ANSWER_LENGTH) {
    const concurrents = corpus?.filter((e) => normalizedLengthOf(e.answer) === mot.length).length ?? 0;
    const estimation = estimateDifficulty(brouillon.mot, { sameLengthCandidates: concurrents });
    difficulteEstimee = { note: estimation.score, raisons: estimation.reasons };
  }

  return {
    nombreDeMots,
    echecs,
    signaux: easeSignals(brouillon.definition),
    couples: corpus === null || mot.length === 0 ? [] : nouveauxCouples(brouillon, corpus),
    difficulteEstimee,
  };
}

/** Ce qui doit être confirmé avant d'écrire : signaux de facilité et couples confondables. */
export function aJuger(controles: Controles): boolean {
  return controles.signaux.length > 0 || controles.couples.length > 0;
}
